/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { Typography, Stack, Box, Tooltip, Zoom, ButtonBase, TextField, InputAdornment } from '@mui/material';
import { CustomAvatar } from '../../../components/custom-avatar';
import { Iconify } from '../../../components';
import { useSelector, dispatch } from '../../../redux/store';
import { getActivities } from '../../../redux/slices/activity';
import capitalizeFirstLetter from '../../../utils/capitalizedFunc';
import { GetPracticeAvatar } from '../../../utils/createAvatar';
import { parseApiDateTime } from '../../../utils/parseApiDateTime';
import {
    callActorName, isFailedVoiceCall, isOutboundVoiceCall, isTimelineCall, isUnansweredInboundCall, voiceCallDetails, voiceCallOutcome, voiceCallSearchText, voiceCallTitle,
} from './timelineCalls';
import TimelineCallEntry from './TimelineCallEntry';
import SharedNumberCalls from './SharedNumberCalls';

// ── Verb → icon + colour ─────────────────────────────────────────────────
// NOTE: hex literals (not palette tokens) — colours flow through `${color}1F`
// interpolations for soft-tinted dot backgrounds; theme tokens like
// `success.main` won't resolve when concatenated as substrings.
const VERB_CONFIG = {
    created: { icon: 'solar:user-plus-bold', color: '#00A76F' },
    treatment_added: { icon: 'solar:teeth-bold', color: '#078DEE' },
    treatment_started: { icon: 'solar:teeth-bold', color: '#00A76F' },
    treatment_changed: { icon: 'solar:teeth-bold', color: '#FFAB00' },
    // Emitted by apps/leads/views.py::update_transition_time whenever a
    // treatment's transition date is edited, from ANY entry point (the
    // Started-date column, the Treatment In Progress sidebar). Amber, not
    // green: this rewrites history that reporting reads off, so it should
    // read as a correction rather than as normal progress.
    treatment_transition_date_changed: { icon: 'solar:calendar-mark-bold', color: '#FFAB00' },
    lead_treatment_status_transitioned: { icon: 'eva:swap-fill', color: '#FFAB00' },
    note_added: { icon: 'solar:pen-bold', color: '#FF5630' },
    message_sent: { icon: 'ic:round-email', color: '#078DEE' },
    message_failed: { icon: 'ic:round-email', color: '#FF5630' },
    whatsapp_message_sent: { icon: 'ic:baseline-whatsapp', color: '#25D366' },
    whatsapp_message_failed: { icon: 'ic:baseline-whatsapp', color: '#FF5630' },
    appointment_added: { icon: 'solar:calendar-bold', color: '#5119B7' },
    appointment_booked: { icon: 'solar:calendar-bold', color: '#5119B7' },
    appointment_attended: { icon: 'eva:checkmark-circle-2-fill', color: '#00A76F' },
    appointment_failed: { icon: 'eva:close-circle-fill', color: '#FF5630' },
    appointment_fta: { icon: 'eva:close-circle-fill', color: '#FF5630' },
    appointment_cancelled: { icon: 'eva:close-circle-fill', color: '#919EAB' },
    appointment_rescheduled: { icon: 'solar:calendar-bold', color: '#FFAB00' },
    lead_archived: { icon: 'mdi:archive-outline', color: '#919EAB' },
    lead_restored: { icon: 'mdi:archive-arrow-up-outline', color: '#078DEE' },
    lead_payment_initiated: { icon: 'solar:wallet-money-bold', color: '#00A76F' },
    stripe_checkout_session_created: { icon: 'solar:wallet-money-bold', color: '#078DEE' },
    stripe_checkout_session_paid: { icon: 'solar:wallet-money-bold', color: '#00A76F' },
    stripe_refund_successful: { icon: 'solar:wallet-money-bold', color: '#FFAB00' },
    stripe_refund_failed: { icon: 'solar:wallet-money-bold', color: '#FF5630' },
    // Task checklist verbs (Tasks V2 — #18)
    task_checklist_email_sent:          { icon: 'ic:round-email',                   color: '#078DEE' },
    task_checklist_whatsapp_sent:       { icon: 'ic:baseline-whatsapp',              color: '#25D366' },
    task_checklist_next_action:         { icon: 'mdi:arrow-right-circle-outline',    color: '#5119B7' },
    task_checklist_status_changed:      { icon: 'eva:swap-fill',                     color: '#FFAB00' },
    task_checklist_attendance_recorded: { icon: 'eva:checkmark-circle-2-fill',       color: '#00A76F' },
    task_checklist_fta_recorded:        { icon: 'eva:close-circle-fill',             color: '#FF5630' },
    task_checklist_treatment_started:   { icon: 'solar:teeth-bold',                  color: '#00A76F' },
    task_checklist_treatment_completed: { icon: 'solar:teeth-bold',                  color: '#5119B7' },
    task_due_date_changed:              { icon: 'solar:calendar-mark-bold',          color: '#FFAB00' },
    task_title_changed:                 { icon: 'solar:pen-bold',                    color: '#5119B7' },
    // Additional task verbs that previously fell to the grey-bell default
    task_checklist_sms_sent:            { icon: 'bi:chat-left-dots-fill',            color: '#7635DC' },
    task_consent_form_sent:             { icon: 'solar:document-text-bold',          color: '#078DEE' },
    task_payment_link_sent:             { icon: 'solar:wallet-money-bold',           color: '#00A76F' },
    task_payment_manually_acknowledged: { icon: 'solar:wallet-money-bold',           color: '#00A76F' },
    task_checklist_phone_called:        { icon: 'fluent:call-20-filled',             color: '#00A76F' },
    default: { icon: 'solar:bell-bold', color: '#919EAB' },
};

// ── Call-outcome icon/label helpers ──────────────────────────────────────────
export const CALL_OUTCOME_ICON = {
    ANSWERED:  { icon: 'fluent:call-20-filled', color: '#00A76F' },
    NO_ANSWER: { icon: 'mdi:phone-missed',      color: '#FF5630' },
    VOICEMAIL: { icon: 'mdi:voicemail',         color: '#FFAB00' },
    BUSY:      { icon: 'mdi:phone-cancel',      color: '#FF8C00' },
    FAILED:    { icon: 'mdi:phone-alert',       color: '#637381' },
};
export const callOutcomeIcon = (outcome) =>
    CALL_OUTCOME_ICON[outcome] || { icon: 'fluent:call-20-filled', color: '#00A76F' };

export const CALL_OUTCOME_LABEL = {
    ANSWERED:  'Answered',
    NO_ANSWER: 'No answer',
    VOICEMAIL: 'Voicemail',
    BUSY:      'Busy',
    FAILED:    'Failed',
};
export const formatCallDuration = (s) => {
    const n = Number(s);
    if (!n || n < 0) return '';
    const m = Math.floor(n / 60);
    const sec = n % 60;
    return m ? `${m}m ${sec}s` : `${sec}s`;
};

const CALL_DIRECTION_LABEL = { inbound: 'Inbound call', outbound: 'Outbound call' };
const CALL_FAILED_OUTCOMES = ['NO_ANSWER', 'BUSY', 'FAILED'];

// ── Clean labels for task-checklist timeline verbs ───────────────────────────
// These verbs produce pre-formatted titles that must bypass capitalizeFirstLetter.
const TASK_VERB_TITLE = {
    task_checklist_whatsapp_sent:         'WhatsApp sent',
    task_checklist_email_sent:            'Email sent',
    task_checklist_sms_sent:              'SMS sent',
    task_checklist_next_action:           'Next action',
    task_consent_form_sent:               'Consent form sent',
    task_payment_link_sent:               'Payment link sent',
    task_payment_manually_acknowledged:   'Payment acknowledged',
    task_checklist_phone_called:          'Phone call',
    task_checklist_attendance_recorded:   'Attendance recorded',
    task_checklist_treatment_started:     'Treatment started',
    task_checklist_treatment_completed:   'Treatment completed',
    task_checklist_fta_recorded:          'Marked FTA',
    task_checklist_status_changed:        'Status changed',
};

// ── FSM state → friendly label (#38) ─────────────────────────────────────────
// Maps the raw lowercase LeadTreatment FSM states carried by
// `lead_treatment_status_transitioned` activities into human-readable text.
const TREATMENT_STATE_LABELS = {
    new: 'New',
    consultation_booked: 'Booked',
    consultation_attended: 'Attended',
    failed_to_attend_consultation: 'Failed to attend',
    treatment_started: 'Treatment started',
    closed: 'Closed',
};

export const humanizeTreatmentState = (raw) => {
    if (raw == null) return '';
    const key = String(raw).toLowerCase();
    if (key in TREATMENT_STATE_LABELS) return TREATMENT_STATE_LABELS[key];
    // Unknown state → Title-Case, underscores→spaces.
    return key
        .split('_')
        .filter(Boolean)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
};

// ── Lead-origin derivation (#42) ─────────────────────────────────────────────
// Maps lead_source.referral enum values to friendly display names.
// UNAVAILABLE and OTHER are intentionally omitted — they are treated as
// "no referral" and fall through to the type map or null.
const REFERRAL_ORIGIN_LABEL = {
    FACEBOOK:                    'Facebook',
    INSTAGRAM:                   'Instagram',
    TIKTOK:                      'TikTok',
    YOUTUBE:                     'YouTube',
    WHATSAPP:                    'WhatsApp',
    PAID_SOCIAL:                 'Paid social',
    ORGANIC_SEARCH:              'Organic search',
    SOCIAL:                      'Social',
    DENTALLY:                    'Dentally',
    SOE:                         'Practice software',
    R4:                          'Practice software',
    PRACTICE_SOFTWARE:           'Practice software',
    EXISTING_PATIENT:            'Existing patient',
    REFERRED_BY_FRIEND:          'Referral (friend)',
    REFERRED_BY_ANOTHER_PRACTICE: 'Referral (practice)',
    REFERRED_BY_ANOTHER_WEBSITE: 'Referral (website)',
    CONSENT_BROADCAST:           'Consent broadcast',
    UNBOUNCE:                    'Unbounce',
    ONLINE_CHAT:                 'Online chat',
    CLINICIAN:                   'Clinician',
    EMAIL:                       'Email',
    VISITED_PRACTICE:            'Practice visit',
    EVENTS:                      'Events',
    BUS:                         'Bus',
    CINEMA:                      'Cinema',
    RADIO:                       'Radio',
    PRINT_ADVERTISING:           'Print advertising',
    OTHER_ONLINE_ADVERTISING:    'Online advertising',
    AI_ASSISTANT:                'AI assistant',
};

// Maps lead_source.type enum values to friendly display names.
// OTHER is intentionally omitted — treated as "no type" and falls to null.
const TYPE_ORIGIN_LABEL = {
    ONLINE_FORM:       'Website',
    ONLINE_BOOKING:    'Online booking',
    WHATSAPP:          'WhatsApp',
    ONLINE_CHAT:       'Online chat',
    PHONE_CALL:        'Phone',
    TELEPHONE:         'Phone',
    EMAIL:             'Email',
    WALK_IN:           'Walk-in',
    VISIT:             'Practice visit',
    QUICK_SEND_INLINE: 'Quick send',
    UNBOUNCE:          'Unbounce',
    CLINICIAN:         'Clinician',
};

// Sentence-case token: underscores → spaces, first word capitalized, rest lower.
const humanizeToken = (v) =>
    String(v).toLowerCase().split('_').filter(Boolean)
        .map((w, i) => (i === 0 ? w[0].toUpperCase() + w.slice(1) : w))
        .join(' ');

/**
 * Derives a human-readable origin label from a lead_source object.
 *
 * Priority:
 *  1. Cal.com  (referral=CALCOM or type=CALCOM)
 *  2. Calendly (referral=CALENDLY or type=CALENDLY)
 *  3. Google Ads (gclid / gbraid / wbraid / gad_campaignid / adwords_conversion_id / referral=PAID_SEARCH)
 *  4. Known referral map (UNAVAILABLE + OTHER skipped → fall-through)
 *  5. Unknown referral → humanizeToken(referral)
 *  6. Known type map (OTHER skipped)
 *  7. Unknown type → humanizeToken(type)
 *  8. null  (no source info)
 *
 * NOTE: metadata={} on the audited Cal.com lead — a virtual-consult variant
 * could key off metadata.booking_type or similar once the schema is confirmed.
 *
 * @param {object|null} leadSource – act.action_object.lead_source
 * @returns {string|null}
 */
export const deriveLeadOrigin = (leadSource) => {
    if (!leadSource) return null;
    const ls = leadSource;

    // 1 — Cal.com
    if (ls.referral === 'CALCOM' || ls.type === 'CALCOM') return 'Cal.com';
    // 2 — Calendly
    if (ls.referral === 'CALENDLY' || ls.type === 'CALENDLY') return 'Calendly';
    // 3 — Google Ads
    if (ls.gclid || ls.gbraid || ls.wbraid || ls.gad_campaignid || ls.adwords_conversion_id || ls.referral === 'PAID_SEARCH') return 'Google Ads';
    // 4/5 — Referral (skip UNAVAILABLE + OTHER)
    if (ls.referral && ls.referral !== 'UNAVAILABLE' && ls.referral !== 'OTHER') {
        return (ls.referral in REFERRAL_ORIGIN_LABEL)
            ? REFERRAL_ORIGIN_LABEL[ls.referral]
            : humanizeToken(ls.referral);
    }
    // 6/7 — Type (skip OTHER)
    if (ls.type && ls.type !== 'OTHER') {
        return (ls.type in TYPE_ORIGIN_LABEL)
            ? TYPE_ORIGIN_LABEL[ls.type]
            : humanizeToken(ls.type);
    }
    return null;
};

/**
 * Effective { source, target } for a `lead_treatment_status_transitioned` activity.
 *
 * Post-BE-#39 rows carry STRUCTURED `data.data.source` / `data.data.target` — those
 * are returned EXACTLY (this keeps the structured path byte-identical to pre-#43).
 * LEGACY rows (created before BE #39) carry NO structured source/target — only the
 * free-text `data.data.message` (e.g. "Lead treatment status transitioned from NEW
 * to CONSULTATION_BOOKED."). For those we parse the message ONCE (the ONLY free-text
 * parse in this module) to recover { source, target }. Unparseable → both undefined
 * (caller then hides nothing / leaves the row standalone with its raw message).
 *
 * @param {object} act – a lead_treatment_status_transitioned activity
 * @returns {{ source: (string|undefined), target: (string|undefined) }}
 */
export const effectiveTransition = (act) => {
    const d = act?.data?.data || {};
    // Structured path (post-#39): return the raw values EXACTLY — byte-identical.
    if (d.source != null || d.target != null) return { source: d.source, target: d.target };
    // Legacy path: parse the BE message. `\s+` absorbs the extra whitespace legacy
    // messages carry, and `[A-Za-z_]+` captures FSM state tokens (e.g. NEW,
    // CONSULTATION_BOOKED).
    const m = String(d.message || '').match(/transitioned from\s+([A-Za-z_]+)\s+to\s+([A-Za-z_]+)/i);
    if (m) return { source: m[1], target: m[2] };
    return { source: undefined, target: undefined };
};

export const getActivityTitle = (act) => {
    if (act?.verb === 'created') {
        const origin = deriveLeadOrigin(act?.action_object?.lead_source);
        return origin ? `Lead created from ${origin}` : 'Lead created.';
    }
    // Desk-phone calls (CU-869fmbgfq): "Missed call", "Outgoing call · 4:12".
    if (isTimelineCall(act)) return voiceCallTitle(act);
    if (act?.verb === 'call_logged') {
        const ao = act.action_object || {};
        // An older backend's WhatsApp entry (CU-869fn2wd1): the same "Missed call" as the rest.
        if (ao.direction === 'inbound' && ao.call_outcome === 'NO_ANSWER') return 'Missed call';
        const dir = CALL_DIRECTION_LABEL[ao.direction] || 'Call';
        const label = CALL_OUTCOME_LABEL[ao.call_outcome] || 'Logged';
        const dur = formatCallDuration(ao.duration);
        return dur && ao.call_outcome === 'ANSWERED' ? `${dir} — ${label} (${dur})` : `${dir} — ${label}`;
    }
    // Fix 1 (#35, option B): a collapsed PHONE pair carries the removed note's
    // `call_outcome` on `_callOutcome`. Render "Phone call — <outcome>" using the
    // SAME format + CALL_OUTCOME_LABEL lookup as the note=OTHER+PHONE branch below,
    // so the label matches the Timeline's phone rendering. Unknown outcome (not in
    // CALL_OUTCOME_LABEL) → plain "Phone call". Task verbs are already flagged
    // pre-formatted in isPreformattedTitle, so capitalize won't mangle it.
    if (act?.verb === 'task_checklist_phone_called' && act?._callOutcome) {
        const label = CALL_OUTCOME_LABEL[act._callOutcome];
        return label ? `Phone call — ${label}` : 'Phone call';
    }
    // Task-checklist verbs → clean pre-formatted label (no free-text from BE).
    if (act?.verb && act.verb in TASK_VERB_TITLE) return TASK_VERB_TITLE[act.verb];
    // Standalone state transition (#38) → relabel to show the CAUSE (user vs system).
    // Cascaded transitions are collapsed into their causative action upstream
    // (collapseTransitions), so any transition reaching here is standalone.
    if (act?.verb === 'lead_treatment_status_transitioned') {
        const d = act?.data?.data || {};
        // #43: resolve source/target via effectiveTransition — STRUCTURED rows return
        // their raw values (byte-identical), LEGACY rows recover them from the message.
        // A standalone LEGACY transition thus relabels via the parsed values → neutral
        // "Treatment moved from New to Booked" (is_system is undefined for legacy).
        const { source, target } = effectiveTransition(act);
        const from = humanizeTreatmentState(source);
        const to = humanizeTreatmentState(target);
        // LEGACY FALLBACK (unparseable only): when effectiveTransition yields no
        // source/target (structured absent AND message unparseable) the #38 relabel
        // would render an awkward, incomplete "Treatment moved to" — so fall back to
        // the raw BE message if it survives, exactly as pre-#38.
        if (!from || !to) {
            const legacyMessage = act?.data?.data?.message;
            if (legacyMessage) return legacyMessage;
        }
        const isClosed = String(target || '').toLowerCase() === 'closed';
        // M-3: only include "from {from}" when the source humanizes to something —
        // a null/empty source must NOT render a dangling "…from  to X".
        const movedTreatment = from ? `moved treatment from ${from} to ${to}` : `moved treatment to ${to}`;
        // M-1: three-way is_system attribution. A legacy event with is_system
        // undefined/absent must NOT be falsely attributed to a user — it degrades
        // to a NEUTRAL "Treatment moved…" row (no actor, no "System").
        if (d.is_system === true) {
            return isClosed ? 'System closed the treatment' : `System ${movedTreatment}`;
        }
        if (d.is_system === false) {
            const actorFirst = act?.actor?.first_name || 'User';
            return isClosed ? `${actorFirst} closed the treatment` : `${actorFirst} ${movedTreatment}`;
        }
        // is_system undefined/absent → neutral, no false attribution.
        if (isClosed) return 'Treatment closed';
        return from ? `Treatment moved from ${from} to ${to}` : `Treatment moved to ${to}`;
    }
    // note_added + note=OTHER → derive title from structured fields, not data.message.
    if (act?.verb === 'note_added' && act?.action_object?.note === 'OTHER') {
        const via = act.action_object?.contacted_via;
        const outcome = act.action_object?.call_outcome;
        if (via === 'PHONE') {
            return outcome ? `Phone call — ${CALL_OUTCOME_LABEL[outcome] || outcome}` : 'Phone call';
        }
        if (via === 'WHATSAPP') return 'WhatsApp';
        if (via === 'EMAIL') return 'Email';
        // 'SMS' is the canonical BE contacted_via; 'TEXT' is a legacy/non-canonical value still accepted defensively for rendering.
        if (via === 'TEXT' || via === 'SMS') return 'SMS';
        return 'Note';
    }
    // #43: a video appointment reads as a "Virtual consultation" (Cal.com when the
    // meeting_url is a cal.com link). Non-video appointments fall through to the
    // existing "Appointment booked to X." message rendering, unchanged.
    if (act?.verb === 'appointment_added') {
        const ao = act?.action_object || {};
        const isVideo = ao.method === 'VIDEO_CALL' || !!ao.meeting_url;
        if (isVideo) {
            return /cal\.com/i.test(ao.meeting_url || '') ? 'Virtual consultation booked via Cal.com' : 'Virtual consultation booked';
        }
        // non-video → fall through to existing default (data.message)
    }
    // Real message verbs (#44, option B) — generic label; channel carried by icon + tooltip.
    if (act?.verb === 'message_sent' || act?.verb === 'whatsapp_message_sent') return 'Message sent';
    if (act?.verb === 'message_failed' || act?.verb === 'whatsapp_message_failed') return 'Message failed';
    return act?.data?.data?.message || '';
};

/**
 * True when the title returned by getActivityTitle is already correctly cased
 * and must NOT pass through capitalizeFirstLetter (which lowercases everything
 * after the first character).  Covers:
 *  - call_logged  (e.g. "Outbound call — No answer", "Missed call")
 *  - task checklist verbs  (e.g. "WhatsApp sent", "SMS sent")
 *  - note_added + note=OTHER  (e.g. "WhatsApp", "Phone call — Answered")
 */
const isPreformattedTitle = (act) =>
    act?.verb === 'call_logged'
    || isTimelineCall(act)
    || act?.verb === 'created'
    || (act?.verb && act.verb in TASK_VERB_TITLE)
    || act?.verb === 'lead_treatment_status_transitioned'
    || (act?.verb === 'note_added' && act?.action_object?.note === 'OTHER')
    // #43: video appointment renders a pre-cased "Virtual consultation booked via
    // Cal.com" — bypass capitalizeFirstLetter so "Cal.com" isn't mangled. Non-video
    // appointments are unaffected (fall through to the normal message pipeline).
    || (act?.verb === 'appointment_added' && (act?.action_object?.method === 'VIDEO_CALL' || act?.action_object?.meeting_url));

/**
 * Returns the final display title for a timeline row.
 *
 * Pre-formatted titles (call_logged, task verbs, note_added+OTHER) are returned
 * verbatim.  All other verbs go through the existing pipeline: Awaiting Response.
 * substitution followed by capitalizeFirstLetter.
 */
export const getDisplayTitle = (act) => {
    if (isPreformattedTitle(act)) return getActivityTitle(act);
    const rawTitle = getActivityTitle(act);
    return rawTitle === 'Awaiting Response.' ? 'Awaiting Reply.' : capitalizeFirstLetter(rawTitle);
};

// ── Message-row channel helpers (#44) ────────────────────────────────────────

/**
 * Returns a human-readable channel label for the 4 real message verbs, or null
 * for all other verbs.  Used for the icon-dot tooltip and unit tests.
 *
 * @param {object} act – activity object
 * @returns {'WhatsApp'|'Auto email'|'Email'|null}
 */
export const messageChannelLabel = (act) => {
    const verb = act?.verb;
    if (verb === 'whatsapp_message_sent' || verb === 'whatsapp_message_failed') return 'WhatsApp';
    if (verb === 'message_sent' || verb === 'message_failed') {
        const ao = act?.action_object;
        if (ao?.message_group === 'SYSTEM' || ao?.message_category === 'SEQUENCE_MESSAGE') return 'Auto email';
        return 'Email';
    }
    return null;
};

/**
 * Returns a secondary preview string for the 4 real message verbs, or ''.
 *
 * - email (message_sent / message_failed): action_object.subject, unless it
 *   starts with '$' (un-resolved merge-tag placeholder).
 * - whatsapp (whatsapp_message_sent / whatsapp_message_failed): the message
 *   preview from data.data.message, with a leading "whatsapp message sent - "
 *   prefix stripped (case-insensitive), trimmed, and truncated to 90 chars.
 *
 * @param {object} act – activity object
 * @returns {string}
 */
export const messageSecondaryText = (act) => {
    const verb = act?.verb;
    if (verb === 'message_sent' || verb === 'message_failed') {
        const subject = act?.action_object?.subject;
        // Blank only unresolved merge-tag syntax ($field / ${field}) — not legit
        // subjects that merely begin with a '$' (e.g. "$50 off consultation").
        if (!subject || /^\$\{?[A-Za-z_]/.test(String(subject))) return '';
        return String(subject);
    }
    if (verb === 'whatsapp_message_sent' || verb === 'whatsapp_message_failed') {
        const raw = act?.data?.data?.message;
        if (!raw) return '';
        const stripped = String(raw).replace(/^whatsapp message (sent|failed)\s*-\s*/i, '').trim();
        if (!stripped) return '';
        return stripped.length > 90 ? `${stripped.slice(0, 90)}…` : stripped;
    }
    return '';
};

export const getActivityIcon = (act) => {
    const verb = act?.verb;
    if (verb === 'note_added') {
        const noteType = act?.action_object?.note;
        const via = act?.action_object?.contacted_via;
        if (noteType === 'UNABLE_TO_CONTACT') return { icon: 'solar:phone-calling-bold', color: '#FF5630' };
        if (via === 'PHONE') {
            return act?.action_object?.call_outcome
                ? callOutcomeIcon(act.action_object.call_outcome)
                : { icon: 'fluent:call-20-filled', color: '#00A76F' };
        }
        if (via === 'WHATSAPP') return { icon: 'ic:baseline-whatsapp', color: '#25D366' };
        if (via === 'EMAIL') return { icon: 'ic:round-email', color: '#078DEE' };
        if (via === 'TEXT' || via === 'SMS') return { icon: 'bi:chat-left-dots-fill', color: '#7635DC' };
        if (noteType === 'FOLLOW_UP') return { icon: 'mdi:clock-time-three-outline', color: '#FFAB00' };
        if (noteType === 'AWAITING_RESPONSE') return { icon: 'mdi:clock-time-three-outline', color: '#078DEE' };
        return { icon: 'solar:pen-bold', color: '#637381' };
    }
    if (verb === 'call_logged') return callOutcomeIcon(act?.action_object?.call_outcome);
    if (isTimelineCall(act)) return callOutcomeIcon(voiceCallOutcome(act));
    if (verb === 'message_sent') {
        const ao = act?.action_object;
        const isAuto = ao?.message_group === 'SYSTEM' || ao?.message_category === 'SEQUENCE_MESSAGE';
        if (isAuto) return { icon: 'mdi:email-fast-outline', color: '#919EAB' };
        return { icon: 'ic:round-email', color: '#078DEE' };
    }
    return VERB_CONFIG[verb] || VERB_CONFIG.default;
};

// ── AI-note parsing ──────────────────────────────────────────────────────
const parseAINote = (noteContent) => {
    if (!noteContent) return null;
    try {
        const parsed = JSON.parse(noteContent);
        if (parsed && parsed.AI_NOTES === true) return parsed;
    } catch (e) { /* not JSON */ }
    return null;
};

const getNoteDisplayText = (noteContent) => {
    const aiData = parseAINote(noteContent);
    if (aiData) return '🤖 AI Call Summary - Click to view in lead details';
    return capitalizeFirstLetter(noteContent);
};

// ── Engagement aggregation ───────────────────────────────────────────────
const ENGAGEMENT_LABELS = {
    delivered: 'Delivered',
    opened: 'Opens',
    clicked: 'Clicks',
    bounced: 'Bounced',
    unsubscribed: 'Unsubscribed',
};

const getEngagementCounts = (action) => {
    if (action?.verb !== 'message_sent' && action?.verb !== 'message_failed') return null;
    if (action?.action_object?.type !== 'EMAIL') return null;
    if (action?.action_object?.message_category === 'NOTIFICATION') return null;
    const counts = {};
    (action?.action_object?.sent_messages || []).forEach((sm) => {
        (sm?.event_counts || []).forEach((ec) => {
            if (ec?.event && ec?.count) counts[ec.event] = (counts[ec.event] || 0) + ec.count;
        });
    });
    return Object.keys(counts).length > 0 ? counts : null;
};

const getEmailStatus = (act) => {
    if (act?.verb !== 'message_sent' && act?.verb !== 'message_failed') return null;
    if (act?.action_object?.type !== 'EMAIL') return null;
    if (act?.action_object?.message_category === 'NOTIFICATION') return null;
    if (act.verb === 'message_failed') return { label: 'Failed', color: '#FF5630', icon: 'eva:close-circle-fill' };
    const counts = getEngagementCounts(act);
    if (counts?.bounced) return { label: 'Bounced', color: '#FF5630', icon: 'solar:close-circle-bold', counts };
    if (counts?.unsubscribed) return { label: 'Unsubscribed', color: '#919EAB', icon: 'mdi:bell-off-outline', counts };
    if (counts?.clicked) return { label: counts.clicked > 1 ? `${counts.clicked} Clicks` : 'Clicked', color: '#00A76F', icon: 'solar:cursor-bold', counts };
    if (counts?.opened) return { label: counts.opened > 1 ? `${counts.opened} Opens` : 'Opened', color: '#3b82f6', icon: 'solar:eye-bold', counts };
    if (counts?.delivered) return { label: 'Delivered', color: '#919EAB', icon: 'eva:done-all-fill', counts };
    const isAuto = act?.action_object?.message_group === 'SYSTEM' || act?.action_object?.message_category === 'SEQUENCE_MESSAGE';
    if (isAuto) return { label: 'Auto', color: '#C4CDD5', icon: 'mdi:robot-outline' };
    return { label: 'Sent', color: '#919EAB', icon: 'mdi:check' };
};

const WHATSAPP_STATUS = {
    read: { label: 'Read', color: '#078DEE', icon: 'eva:done-all-fill' },
    delivered: { label: 'Delivered', color: '#919EAB', icon: 'eva:done-all-fill' },
    sent: { label: 'Sent', color: '#C4CDD5', icon: 'mdi:check' },
    failed: { label: 'Failed', color: '#FF5630', icon: 'eva:close-fill' },
};

const getWhatsAppStatus = (act) => {
    if (act?.verb === 'whatsapp_message_failed') return { label: 'Failed', color: '#FF5630', icon: 'eva:close-circle-fill' };
    if (act?.verb !== 'whatsapp_message_sent') return null;
    return WHATSAPP_STATUS[act?.action_object?.status || 'sent'] || WHATSAPP_STATUS.sent;
};

const buildStatusTooltip = (status) => {
    if (!status?.counts) return status?.label || '';
    return Object.entries(ENGAGEMENT_LABELS)
        .filter(([k]) => status.counts[k])
        .map(([k, label]) => `${status.counts[k]} ${label}`)
        .join(' · ');
};

// ── Filter pills ─────────────────────────────────────────────────────────
export const categorizeActivity = (activity) => {
    const verb = activity?.verb;
    const note = activity?.action_object?.note;
    const via = activity?.action_object?.contacted_via;
    const buckets = [];
    if (verb === 'note_added') {
        if (via === 'PHONE') {
            buckets.push('calls');
            if (CALL_FAILED_OUTCOMES.includes(activity?.action_object?.call_outcome)) buckets.push('failed');
        } else if (via === 'EMAIL') buckets.push('emails');
        else if (via === 'WHATSAPP') buckets.push('whatsapp');
        else if (via === 'TEXT' || via === 'SMS') buckets.push('sms');
        if (note === 'UNABLE_TO_CONTACT') buckets.push('failed');
    } else if (verb === 'message_sent') buckets.push('emails');
    else if (verb === 'message_failed') buckets.push('emails', 'failed');
    else if (verb === 'whatsapp_message_sent') buckets.push('whatsapp');
    else if (verb === 'whatsapp_message_failed') buckets.push('whatsapp', 'failed');
    else if (verb === 'call_logged') {
        buckets.push('calls');
        if (CALL_FAILED_OUTCOMES.includes(activity?.action_object?.call_outcome)) buckets.push('failed');
    }
    else if (isTimelineCall(activity)) {
        buckets.push('calls');
        if (isFailedVoiceCall(activity)) buckets.push('failed');
    }
    else if (verb === 'appointment_added' || verb === 'appointment_rescheduled' || verb === 'appointment_cancelled') {
        buckets.push('appointments');
    }
    return buckets.length > 0 ? buckets : ['other'];
};

const SUMMARY_PILLS = [
    { key: 'all', label: 'All', icon: 'mdi:format-list-bulleted', color: '#637381' },
    { key: 'calls', label: 'Calls', icon: 'fluent:call-20-filled', color: '#00A76F' },
    { key: 'emails', label: 'Emails', icon: 'eva:email-fill', color: '#1890FF' },
    { key: 'whatsapp', label: 'WhatsApp', icon: 'ri:whatsapp-fill', color: '#25D366' },
    { key: 'sms', label: 'SMS', icon: 'bi:chat-left-dots-fill', color: '#7635DC' },
    { key: 'appointments', label: 'Appointments', icon: 'mdi:calendar-check', color: '#FF6C40' },
    { key: 'failed', label: 'Failed', icon: 'mdi:alert-circle-outline', color: '#FF5630' },
];

const INITIAL_FETCH_LIMIT = 100;
const SCROLL_FETCH_BATCH = 25;

// Smart relative time — uses fuzzy text for recent events, absolute for older.
export const formatActivityTime = (timestamp) => {
    const m = parseApiDateTime(timestamp);
    if (!m) return '';
    const now = moment();
    const diffHours = now.diff(m, 'hours');
    const diffDays = now.diff(m.clone().startOf('day'), 'days');
    if (diffHours < 24 && diffDays === 0) return m.fromNow(); // "2 hours ago"
    if (diffDays === 1) return `Yesterday at ${m.format('h:mm a')}`;
    if (diffDays > 1 && diffDays <= 6) return `${m.format('dddd')} at ${m.format('h:mm a')}`; // "Wednesday at 3:14 pm"
    if (m.year() === now.year()) return m.format('DD MMM · h:mm a');
    return m.format('DD MMM YYYY · h:mm a');
};

// ── State-transition collapse / dedup (#38) ──────────────────────────────────
// A single user (or system) action cascades a lead treatment through the FSM and
// the backend emits BOTH a specific action verb (appointment_added, …) AND a
// generic `lead_treatment_status_transitioned`. We collapse the redundant generic
// row into its causative action so the timeline shows the human CAUSE, never a
// bare "system transitioned…" row when a user actually did it.

// Specific action verbs that own a cascaded transition.
// M-2: `appointment_booked` is included defensively — a booking may be emitted as
// either `appointment_added` OR `appointment_booked`, and either should absorb the
// consultation_booked transition.
const SPECIFIC_TRANSITION_VERBS = ['appointment_added', 'appointment_booked', 'appointment_attended', 'appointment_fta', 'treatment_started'];

// Time-proximity tolerance for collapsing a cascaded transition into its causative
// specific action. The BE emits the specific verb and its cascaded transition from
// the SAME request but NOT atomically — on staging they were observed ~1.1s apart,
// crossing a wall-clock second boundary. A generous 60s window absorbs that jitter
// while still keeping genuinely distinct actions (e.g. a booking at 10:00 and an
// attendance at 14:00) in separate collapses via nearest-match selection.
const TRANSITION_COLLAPSE_WINDOW_SECONDS = 60;

// Accepted causative specific verbs for a given FSM target state.
// A cascaded transition collapses into a specific sibling ONLY when that sibling's
// verb is one of the accepted causative verbs for the transition's target. There is
// NO "nearest unrelated specific" fallback — a target with no accepted verb in-window
// (or an unmapped target such as `closed`) stays standalone. This prevents over-
// collapsing a manual/unrelated transition into a coincidental nearby appointment row.
// consultation_booked accepts BOTH `appointment_added` and `appointment_booked` (M-2).
const TARGET_TO_SPECIFIC_VERB = {
    consultation_booked: ['appointment_added', 'appointment_booked'],
    consultation_attended: ['appointment_attended'],
    failed_to_attend_consultation: ['appointment_fta'],
    treatment_started: ['treatment_started'],
};

// LeadTreatment id linkage differs by verb (see BE contract):
//  - transitioned / treatment_started → action_object.id
//  - appointment_*                    → action_object.lead_treatment
const transitionTreatmentId = (act) => {
    if (!act) return undefined;
    const v = act.verb;
    if (v === 'lead_treatment_status_transitioned' || v === 'treatment_started') return act?.action_object?.id;
    if (v === 'appointment_added' || v === 'appointment_booked' || v === 'appointment_attended' || v === 'appointment_fta') {
        return act?.action_object?.lead_treatment;
    }
    return undefined;
};

// Normalize an activity timestamp to epoch-ms via parseApiDateTime — BE emits
// `DD-MM-YYYY HH:mm:ss[.ffffff]Z` as UK LOCAL wall-clock time (the trailing 'Z'
// is a misleading literal, not a UTC marker); ISO-8601 with a real offset is
// also accepted. Using the shared helper (rather than a bespoke Date.parse)
// keeps ordering/dedupe consistent with what's displayed, and correct across
// the BST/GMT clock-change instead of merely self-consistent.
const timestampToEpochMs = (ts) => {
    const m = parseApiDateTime(ts);
    return m ? m.valueOf() : NaN;
};

// Pure, testable absolute time delta (in whole seconds) between two activity
// timestamps. Unparseable input → Infinity (never collapses → safe standalone).
export const activityDeltaSeconds = (tsA, tsB) => {
    const a = timestampToEpochMs(tsA);
    const b = timestampToEpochMs(tsB);
    if (Number.isNaN(a) || Number.isNaN(b)) return Infinity;
    return Math.abs(a - b) / 1000;
};

// Pick the specific action (by index) that a transition detail should attach to,
// among candidates already known to be within the collapse window. ONLY a specific
// whose verb is an accepted causative verb for the transition's `target` qualifies;
// among those, choose the NEAREST by absolute time delta. If the target is unmapped
// (e.g. `closed`) or no accepted verb is in-window, return null → the transition
// stays standalone. There is deliberately NO unrelated-nearest fallback.
const chooseSpecificIndex = (specificIdxs, items, target, transitionTs) => {
    const accepted = TARGET_TO_SPECIFIC_VERB[String(target || '').toLowerCase()] || [];
    if (accepted.length === 0) return null;
    const candidates = specificIdxs.filter((i) => accepted.includes(items[i]?.verb));
    if (candidates.length === 0) return null;
    let best = candidates[0];
    let bestDelta = activityDeltaSeconds(transitionTs, items[best]?.timestamp);
    candidates.forEach((i) => {
        const delta = activityDeltaSeconds(transitionTs, items[i]?.timestamp);
        if (delta < bestDelta) { best = i; bestDelta = delta; }
    });
    return best;
};

/**
 * Pre-process the raw activity list (#38):
 *  1. Cascaded transition → collapse the generic `lead_treatment_status_transitioned`
 *     into its causative specific action row (attaches `_transitionDetail`).
 *  2/3. Standalone transition (no specific sibling) is kept — its title is relabelled
 *     by getActivityTitle (user vs system, closed special-case).
 *  4. No-op transitions (source === target) are dropped.
 * Pure: rows that receive `_transitionDetail` are shallow-cloned; redux items untouched.
 *
 * I-1 (grouping): activities are grouped by treatment id ONLY, then a transition is
 * collapsed into a specific sibling only when they fall within
 * `TRANSITION_COLLAPSE_WINDOW_SECONDS` of each other. The BE emits the causative
 * specific verb AND its cascaded `lead_treatment_status_transitioned` from the same
 * request but NOT atomically — on staging they were ~1.1s apart and straddled a
 * second boundary, so the old exact-second bucket left the transition standalone
 * (two rows instead of one). A proximity window fixes that; a transition with NO
 * specific sibling in-window still degrades to a standalone relabelled row.
 */
export const collapseTransitions = (items) => {
    if (!Array.isArray(items)) return [];

    const toRemove = new Set();          // indices dropped from output
    const detailForIdx = new Map();      // specific-action index → { source, target }
    const groups = new Map();            // treatmentId → { transitions:[], specifics:[] }

    items.forEach((act, idx) => {
        const isTransition = act?.verb === 'lead_treatment_status_transitioned';
        if (isTransition) {
            // #43: use effectiveTransition so LEGACY no-ops (e.g. "…from NEW to NEW.",
            // no structured fields) are also dropped, not just structured new→new.
            const { source, target } = effectiveTransition(act);
            // No-op transition (e.g. new→new) → drop entirely.
            if (source != null && target != null && source === target) { toRemove.add(idx); return; }
        }
        const isSpecific = SPECIFIC_TRANSITION_VERBS.includes(act?.verb);
        if (!isTransition && !isSpecific) return;
        const tid = transitionTreatmentId(act);
        if (tid == null) return;
        const key = String(tid);
        if (!groups.has(key)) groups.set(key, { transitions: [], specifics: [] });
        if (isTransition) groups.get(key).transitions.push(idx);
        else groups.get(key).specifics.push(idx);
    });

    groups.forEach(({ transitions, specifics }) => {
        if (transitions.length === 0 || specifics.length === 0) return;
        transitions.forEach((tIdx) => {
            // #43: effective source/target — STRUCTURED rows return raw values
            // (byte-identical), LEGACY rows recover them from the message so a legacy
            // NEW→CONSULTATION_BOOKED collapses into its causative appointment too.
            const { source, target } = effectiveTransition(items[tIdx]);
            const tTs = items[tIdx]?.timestamp;
            // Only specific siblings within the tolerance window are collapse candidates.
            const inWindow = specifics.filter(
                (sIdx) => activityDeltaSeconds(tTs, items[sIdx]?.timestamp) <= TRANSITION_COLLAPSE_WINDOW_SECONDS
            );
            // No specific within the window → leave the transition standalone (relabelled).
            if (inWindow.length === 0) return;
            const chosen = chooseSpecificIndex(inWindow, items, target, tTs);
            // No accepted causative verb in-window (unrelated specific, or unmapped
            // target like `closed`) → leave the transition STANDALONE (relabelled),
            // never collapse into a semantically-wrong sibling.
            if (chosen == null) return;
            // I-2: if ≥2 transitions in a group resolve to the SAME specific index,
            // keep the FIRST (transitions are in ascending item order → deterministic)
            // rather than silently overwriting its detail. Both transition rows are
            // still dropped below regardless.
            if (!detailForIdx.has(chosen)) detailForIdx.set(chosen, { source, target });
            toRemove.add(tIdx);
        });
    });

    const out = [];
    items.forEach((act, idx) => {
        if (toRemove.has(idx)) return;
        out.push(detailForIdx.has(idx) ? { ...act, _transitionDetail: detailForIdx.get(idx) } : act);
    });
    return out;
};

// ── Note/task-checklist pair dedup (#35) ─────────────────────────────────────
// Completing a checklist contact action emits BOTH a note_added (note=OTHER,
// contacted_via=CHANNEL) and a task_checklist_<channel> row from the same
// user action. We hide the redundant note_added and keep the task_checklist
// row, which renders the clean pre-formatted label via TASK_VERB_TITLE.

// task_checklist verb → contacted_via values it pairs with.
const TASK_CHANNEL_TO_CONTACTED_VIA = {
    task_checklist_whatsapp_sent: ['WHATSAPP'],
    task_checklist_email_sent:    ['EMAIL'],
    task_checklist_sms_sent:      ['SMS', 'TEXT'],
    task_checklist_phone_called:  ['PHONE'],
};

// contacted_via → task verb (inverted for fast note-side lookup).
const CONTACTED_VIA_TO_TASK_VERB = {};
Object.entries(TASK_CHANNEL_TO_CONTACTED_VIA).forEach(([verb, vias]) => {
    vias.forEach((via) => { CONTACTED_VIA_TO_TASK_VERB[via] = verb; });
});

// Actor identity: prefer numeric id, fall back to email or username.
// Returns undefined when actor is absent/anonymous → no-match.
const actorKey = (actor) => {
    if (!actor) return undefined;
    if (actor.id != null) return String(actor.id);
    if (actor.email) return String(actor.email);
    if (actor.username) return String(actor.username);
    return undefined;
};

// Treatment id from an activity's action_object.
// Returns undefined when absent → criterion skipped (see spec).
const treatmentIdFromAo = (act) => {
    const ao = act?.action_object;
    if (!ao) return undefined;
    const id = ao.lead_treatment ?? ao.treatment ?? undefined;
    return id != null ? String(id) : undefined;
};

const NOTE_TASK_COLLAPSE_WINDOW_SECONDS = 60;

/**
 * Pre-process the raw activity list (#35):
 * Drops `note_added` (note=OTHER + contacted_via in channel set) rows when a
 * matching `task_checklist_<channel>` row exists from the same actor within
 * 60 seconds (and same treatment when both expose one).
 *
 * Keeps the task_checklist row, which renders the clean pre-formatted label
 * ("WhatsApp sent", "Email sent", "SMS sent", "Phone call") via TASK_VERB_TITLE.
 * Never removes: whatsapp_message_sent/failed, message_sent/failed, call_logged,
 * standalone notes (no matching task), or meaningful note sub-types
 * (UNABLE_TO_CONTACT, FOLLOW_UP, AWAITING_RESPONSE) — those are not note=OTHER.
 *
 * When a checklist action and its audit note collapse into one row, the note's
 * operator-entered detail is preserved on the kept checklist row. This keeps the
 * timeline compact without losing the call outcome, duration or notes recorded
 * while completing the checklist item.
 *
 * KNOWN EDGE (spec-accepted heuristic trade-off): a coincident INDEPENDENT same-actor
 * note=OTHER row whose contacted_via matches the channel, emitted within the 60s window,
 * CAN be suppressed even though it wasn't the checklist action's own note. This is an
 * accepted false-positive of the proximity+actor+channel heuristic.
 *
 * Pure: does not mutate input items (enriched rows are shallow-cloned).
 */
export const collapseNoteTaskPairs = (items) => {
    if (!Array.isArray(items)) return [];

    // Index task_checklist rows by verb.
    const tasksByVerb = new Map(
        Object.keys(TASK_CHANNEL_TO_CONTACTED_VIA).map((v) => [v, []])
    );
    items.forEach((act, idx) => {
        if (act?.verb && tasksByVerb.has(act.verb)) tasksByVerb.get(act.verb).push(idx);
    });

    // Build all valid (noteIdx, taskIdx, delta) pairs.
    const pairs = [];
    items.forEach((act, noteIdx) => {
        if (act?.verb !== 'note_added') return;
        if (act?.action_object?.note !== 'OTHER') return;
        const via = act?.action_object?.contacted_via;
        if (!via) return;
        const taskVerb = CONTACTED_VIA_TO_TASK_VERB[via];
        if (!taskVerb) return;

        const noteActor = actorKey(act.actor);
        const noteTid = treatmentIdFromAo(act);
        const noteTs = act?.timestamp;

        (tasksByVerb.get(taskVerb) || []).forEach((taskIdx) => {
            const taskAct = items[taskIdx];
            // Both must have resolvable, matching actor identities.
            const taskActor = actorKey(taskAct?.actor);
            if (noteActor === undefined || taskActor === undefined) return;
            if (noteActor !== taskActor) return;
            // Within 60 seconds.
            const delta = activityDeltaSeconds(noteTs, taskAct?.timestamp);
            if (delta > NOTE_TASK_COLLAPSE_WINDOW_SECONDS) return;
            // Treatment id (only when both rows expose one).
            const taskTid = treatmentIdFromAo(taskAct);
            if (noteTid !== undefined && taskTid !== undefined && noteTid !== taskTid) return;
            pairs.push({ noteIdx, taskIdx, delta });
        });
    });

    // Sort ascending by delta so nearest pairs are assigned first.
    pairs.sort((a, b) => a.delta - b.delta);

    // Greedy bipartite assignment: each note and each task used at most once.
    const usedNotes = new Set();
    const usedTasks = new Set();
    const toRemove = new Set();
    // Preserve audit-note detail on the surviving task row. The task action is
    // still the canonical event, while these private fields make the associated
    // operator note visible to compact consumers such as the FollowUp sidebar.
    const noteDetailForTask = new Map();
    pairs.forEach(({ noteIdx, taskIdx }) => {
        if (usedNotes.has(noteIdx) || usedTasks.has(taskIdx)) return;
        usedNotes.add(noteIdx);
        usedTasks.add(taskIdx);
        toRemove.add(noteIdx);
        const noteAction = items[noteIdx]?.action_object || {};
        const isPhoneChecklistAction = items[taskIdx]?.verb === 'task_checklist_phone_called';
        const detail = {
            outcome: isPhoneChecklistAction ? noteAction.call_outcome : undefined,
            duration: isPhoneChecklistAction ? noteAction.duration : undefined,
            notes: noteAction.additional_notes,
        };
        if (detail.outcome || detail.duration || detail.notes) noteDetailForTask.set(taskIdx, detail);
    });

    // Emit kept rows; shallow-clone enriched rows (never mutate redux items —
    // mirrors the `_transitionDetail` clone pattern).
    const out = [];
    items.forEach((act, idx) => {
        if (toRemove.has(idx)) return;
        const detail = noteDetailForTask.get(idx);
        out.push(detail ? {
            ...act,
            ...(detail.outcome ? { _callOutcome: detail.outcome } : {}),
            ...(detail.duration ? { _callDuration: detail.duration } : {}),
            ...(detail.notes ? { _additionalNotes: detail.notes } : {}),
        } : act);
    });
    return out;
};

// Outbound contact = something we sent OUT to the lead.
export const isOutboundContact = (act) => {
    const v = act?.verb;
    if (v === 'message_sent' || v === 'whatsapp_message_sent') return true;
    if (v === 'call_logged') return act?.action_object?.direction === 'outbound';
    if (isTimelineCall(act)) return isOutboundVoiceCall(act);
    if (v === 'note_added') {
        const noteType = act?.action_object?.note;
        return noteType === 'FOLLOW_UP' || noteType === 'AWAITING_RESPONSE';
    }
    return false;
};

// Searches title text, actor name, additional notes, email subject, and synthesized call titles.
export const matchesSearch = (act, query) => {
    if (!query) return true;
    const q = query.trim().toLowerCase();
    if (!q) return true;
    const haystack = [
        getActivityTitle(act),
        // A call's actor is a fallback (practice owner); search its person instead.
        isTimelineCall(act) || isUnansweredInboundCall(act) ? '' : act?.actor?.first_name,
        isTimelineCall(act) || isUnansweredInboundCall(act) ? '' : act?.actor?.last_name,
        act?.action_object?.additional_notes,
        act?.action_object?.subject,
        act?.action_object?.body,
        isTimelineCall(act) ? voiceCallSearchText(act) : '',
    ].filter(Boolean).join(' ').toLowerCase();
    return haystack.includes(q);
};

// Group activities into Today / Yesterday / This week / Earlier buckets.
const groupByDate = (items) => {
    const today = moment().startOf('day');
    const yesterday = today.clone().subtract(1, 'day');
    const weekStart = today.clone().subtract(6, 'days'); // last 7 days incl today
    const groups = [
        { key: 'today', label: 'Today', items: [] },
        { key: 'yesterday', label: 'Yesterday', items: [] },
        { key: 'thisWeek', label: 'This week', items: [] },
        { key: 'earlier', label: 'Earlier', items: [] },
    ];
    items.forEach((item) => {
        const ts = parseApiDateTime(item.timestamp);
        if (!ts) { groups[3].items.push(item); return; }
        if (ts.isSameOrAfter(today)) groups[0].items.push(item);
        else if (ts.isSameOrAfter(yesterday)) groups[1].items.push(item);
        else if (ts.isSameOrAfter(weekStart)) groups[2].items.push(item);
        else groups[3].items.push(item);
    });
    return groups.filter((g) => g.items.length > 0);
};

const Activities = (data) => {
    const { practiceTreatment, practiceId } = useSelector((state) => state.practiceTreatmentList);
    const { activityList: rawActivityList } = useSelector((state) => state.activity);
    // GDPR guard (RCA 2026-06-19): activityList is a single GLOBAL slice shared by every
    // surface. Only render activities belonging to the CURRENT lead so a previously
    // viewed lead's timeline can't leak in during/after the fetch. Shadowing the name
    // guards every consumer below for free.
    const activityList = String(rawActivityList?._leadId) === String(data?.leadId)
        ? rawActivityList
        : { results: [], count: 0 };
    const [filter, setFilter] = useState('all');
    const [search, setSearch] = useState('');
    const [expandedId, setExpandedId] = useState(null);

    useEffect(() => {
        if (data?.leadId) {
            dispatch(getActivities(practiceId, data?.leadId, INITIAL_FETCH_LIMIT, 0));
            setFilter('all');
            setSearch('');
            setExpandedId(null);
        }
    }, [data?.leadId]);

    const handleScroll = (e) => {
        const bottom = e.target.scrollHeight - e.target.scrollTop <= e.target.clientHeight + 1;
        if (bottom && Array.isArray(activityList?.results) && activityList?.count > activityList?.results?.length) {
            dispatch(getActivities(practiceId, data?.leadId, SCROLL_FETCH_BATCH, activityList?.results?.length));
        }
    };

    // #38: collapse cascaded state transitions into their causative action and
    // drop no-op transitions; #35: drop redundant note_added rows that are
    // paired with a task_checklist_<channel> row from the same user action.
    // Both passes are pure and target different verbs — compose them.
    const collapsedItems = useMemo(
        () => collapseNoteTaskPairs(collapseTransitions(activityList?.results || [])),
        [activityList?.results]
    );

    const counts = useMemo(() => {
        const result = { all: 0, calls: 0, emails: 0, whatsapp: 0, sms: 0, appointments: 0, failed: 0 };
        result.all = collapsedItems.length;
        collapsedItems.forEach((item) => {
            categorizeActivity(item).forEach((b) => {
                if (b in result) result[b] += 1;
            });
        });
        return result;
    }, [collapsedItems]);

    const filteredItems = useMemo(() =>
        collapsedItems.filter((item) =>
            (filter === 'all' || categorizeActivity(item).includes(filter))
            && matchesSearch(item, search)
        ), [collapsedItems, filter, search]);

    const groupedItems = useMemo(() => groupByDate(filteredItems), [filteredItems]);

    // Most recent outbound contact id — used to mark the row with a subtle ring.
    const latestOutboundId = useMemo(() => {
        const items = activityList?.results || [];
        const found = items.find((it) => isOutboundContact(it));
        return found ? (found.id || null) : null;
    }, [activityList?.results]);

    // Stats banner — total + last contact relative time.
    const lastContactText = useMemo(() => {
        const items = activityList?.results || [];
        const last = items.find((it) => isOutboundContact(it));
        if (!last) return null;
        const m = parseApiDateTime(last.timestamp);
        if (!m) return null;
        return m.fromNow();
    }, [activityList?.results]);

    const totalLoaded = activityList?.results?.length || 0;
    const totalAvailable = activityList?.count || 0;
    const hasMore = totalAvailable > totalLoaded;

    return (
        <Box
            onScroll={handleScroll}
            sx={{
                maxHeight: data?.type === 'contactLeadPopup' ? 'calc(100vh - 220px)' : '880px',
                overflow: 'auto',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                bgcolor: 'background.paper',
            }}
        >
            {/* Sticky header — search + filter pills */}
            <Box sx={{
                position: 'sticky',
                top: 0,
                zIndex: 3,
                bgcolor: 'background.paper',
                px: 2.5,
                pt: 2,
                pb: 1.5,
                borderBottom: '1px solid',
                borderColor: 'divider',
            }}>
                <TextField
                    fullWidth
                    size="small"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search messages, notes, people…"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Iconify icon="eva:search-fill" sx={{ width: 18, height: 18, color: 'text.disabled' }} />
                            </InputAdornment>
                        ),
                        endAdornment: search ? (
                            <InputAdornment position="end">
                                <ButtonBase onClick={() => setSearch('')} sx={{ p: 0.25, borderRadius: '50%' }}>
                                    <Iconify icon="eva:close-fill" sx={{ width: 16, height: 16, color: 'text.disabled' }} />
                                </ButtonBase>
                            </InputAdornment>
                        ) : null,
                        sx: { fontSize: '0.825rem', borderRadius: 1 },
                    }}
                    sx={{ mb: 1.25 }}
                />
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                    {SUMMARY_PILLS.map((pill) => {
                        const count = counts[pill.key] ?? 0;
                        const isActive = filter === pill.key;
                        const isDisabled = pill.key !== 'all' && count === 0;
                        return (
                            <ButtonBase
                                key={pill.key}
                                disabled={isDisabled}
                                onClick={() => setFilter(pill.key)}
                                sx={{
                                    px: 1.25, py: 0.5, borderRadius: 1,
                                    bgcolor: isActive ? pill.color : 'background.paper',
                                    border: '1px solid',
                                    borderColor: isActive ? pill.color : 'divider',
                                    color: isActive ? 'common.white' : 'text.primary',
                                    opacity: isDisabled ? 0.4 : 1,
                                    transition: 'all 0.15s',
                                    '&:hover:not(:disabled)': { bgcolor: isActive ? pill.color : 'action.hover' },
                                }}
                            >
                                <Stack direction="row" alignItems="center" spacing={0.75}>
                                    <Iconify icon={pill.icon} sx={{ width: 14, height: 14, color: isActive ? 'common.white' : pill.color }} />
                                    <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>{pill.label}</Typography>
                                    <Box sx={{
                                        px: 0.75, py: 0.1, borderRadius: 4,
                                        bgcolor: isActive ? 'rgba(255,255,255,0.25)' : 'action.hover',
                                        fontSize: '0.7rem', fontWeight: 700, minWidth: 18, textAlign: 'center',
                                    }}>{count}</Box>
                                </Stack>
                            </ButtonBase>
                        );
                    })}
                </Stack>
            </Box>

            {/* Calls from a shared number that may be this lead (CU-869fmbgfq) */}
            {data?.type === 'leadDetail' && data?.leadId && (
                <SharedNumberCalls
                    practiceId={practiceId}
                    leadId={data.leadId}
                    onAttached={() => dispatch(getActivities(practiceId, data.leadId, INITIAL_FETCH_LIMIT, 0))}
                />
            )}

            {/* Stats banner */}
            {totalLoaded > 0 && (
                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{
                        px: 2.5,
                        py: 1.25,
                        bgcolor: 'background.neutral',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        flexWrap: 'wrap',
                    }}
                >
                    <Iconify icon="solar:graph-up-bold" sx={{ width: 16, height: 16, color: 'text.secondary' }} />
                    {(() => {
                        // Pluralise against whatever count we actually display, otherwise
                        // we render "1 activities" when totalAvailable=0 but totalLoaded=1.
                        const shown = totalAvailable || totalLoaded;
                        return (
                            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.primary' }}>
                                {shown} {shown === 1 ? 'activity' : 'activities'}
                            </Typography>
                        );
                    })()}
                    {lastContactText && (
                        <>
                            <Box sx={{ width: 3, height: 3, bgcolor: 'text.disabled', borderRadius: '50%' }} />
                            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                                last contacted <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>{lastContactText}</Box>
                            </Typography>
                        </>
                    )}
                    {(filter !== 'all' || search) && (
                        <>
                            <Box sx={{ width: 3, height: 3, bgcolor: 'text.disabled', borderRadius: '50%' }} />
                            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                                showing <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>{filteredItems.length}</Box>
                            </Typography>
                        </>
                    )}
                </Stack>
            )}

            {/* Feed */}
            <Box sx={{ px: 2.5, py: 2 }}>
                {filteredItems.length === 0 && totalLoaded > 0 && (
                    <Box sx={{ py: 5, textAlign: 'center' }}>
                        <Iconify icon="eva:inbox-outline" sx={{ width: 32, height: 32, color: 'text.disabled', mb: 1 }} />
                        <Typography sx={{ fontSize: '0.825rem', color: 'text.secondary', fontWeight: 500 }}>
                            No matches{search ? ` for "${search}"` : ''}
                            {hasMore ? ' — scroll for more' : ''}
                        </Typography>
                    </Box>
                )}

                {groupedItems.map((group, groupIdx) => (
                    <Box key={group.key} sx={{ mt: groupIdx === 0 ? 0 : 2.5 }}>
                        {/* Date header */}
                        <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1.25}
                            sx={{ mb: 1.5 }}
                        >
                            <Typography sx={{
                                fontSize: '0.7rem',
                                fontWeight: 700,
                                letterSpacing: 0.6,
                                textTransform: 'uppercase',
                                color: 'text.secondary',
                                whiteSpace: 'nowrap',
                            }}>
                                {group.label}
                            </Typography>
                            <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(145,158,171,0.16)' }} />
                            <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled', fontWeight: 600 }}>
                                {group.items.length}
                            </Typography>
                        </Stack>

                        {/* Group rows */}
                        {group.items.map((act, index) => {
                            const isLast = index === group.items.length - 1;
                            const { icon: dotIcon, color: dotColor } = getActivityIcon(act);
                            const status = getEmailStatus(act) || getWhatsAppStatus(act);
                            const isExpandable =
                                act.verb === 'whatsapp_message_sent'
                                || act.verb === 'whatsapp_message_failed'
                                || act.verb === 'message_sent'
                                || act.verb === 'message_failed';
                            const rowKey = act.id || `${group.key}-${index}`;
                            const isExpanded = expandedId === rowKey;
                            const isLatest = act.id && act.id === latestOutboundId;
                            const title = getDisplayTitle(act);
                            const dateStr = formatActivityTime(act.timestamp);
                            // A call's actor is a fallback (practice owner, record editor); name the person
                            // on the call instead, and nobody for a missed inbound call (CU-869fn2wd1).
                            const actorName = callActorName(act);
                            // A checklist action can replace its paired audit note in
                            // collapseNoteTaskPairs(). Read the preserved fields too,
                            // so compacting the timeline never hides what the operator
                            // recorded while completing that checklist item.
                            const additionalNotes = act?.action_object?.additional_notes || act?._additionalNotes;
                            const channelLabel = messageChannelLabel(act);
                            const secondaryText = isTimelineCall(act) ? voiceCallDetails(act) : messageSecondaryText(act);
                            const callOutcome = act?.action_object?.call_outcome || act?._callOutcome || null;
                            const callDurationText = callOutcome
                                ? formatCallDuration(act?.action_object?.duration || act?._callDuration)
                                : '';

                            return (
                                <Box
                                    key={rowKey}
                                    sx={{
                                        position: 'relative',
                                        display: 'flex',
                                        pb: isLast ? 0 : 2.25,
                                    }}
                                >
                                    {!isLast && (
                                        <Box sx={{
                                            position: 'absolute',
                                            left: 15,
                                            top: 34,
                                            bottom: 0,
                                            width: '1px',
                                            bgcolor: 'rgba(145,158,171,0.16)',
                                        }} />
                                    )}

                                    {/* Dot */}
                                    {(() => {
                                        // Combine channel label + "Most recent contact" into a single
                                        // outer tooltip.  MUI does not support nested Tooltips, so the
                                        // green-ring's own tooltip is only rendered when there is no
                                        // channel label (otherwise the outer tooltip carries both).
                                        const dotTooltipTitle = [channelLabel, isLatest ? 'Most recent contact' : ''].filter(Boolean).join(' · ');
                                        const greenRing = isLatest && (
                                            <Box sx={{
                                                position: 'absolute',
                                                inset: -3,
                                                borderRadius: '50%',
                                                border: '2px solid',
                                                borderColor: 'success.main',
                                                opacity: 0.35,
                                            }} />
                                        );
                                        return (
                                    <Tooltip title={dotTooltipTitle} placement="top" disableHoverListener={!dotTooltipTitle} disableFocusListener={!dotTooltipTitle} disableTouchListener={!dotTooltipTitle}>
                                        <Box sx={{
                                            position: 'relative',
                                            width: 32,
                                            height: 32,
                                            flexShrink: 0,
                                            zIndex: 1,
                                            mt: '1px',
                                        }}>
                                            {isLatest && (
                                                channelLabel
                                                    ? greenRing
                                                    : (
                                                        <Tooltip title="Most recent contact" placement="top">
                                                            {greenRing}
                                                        </Tooltip>
                                                    )
                                            )}
                                            <Box sx={{
                                                width: 32,
                                                height: 32,
                                                borderRadius: '50%',
                                                bgcolor: `${dotColor}1F`,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}>
                                                <Iconify icon={dotIcon} sx={{ width: 16, height: 16, color: dotColor }} />
                                            </Box>
                                        </Box>
                                    </Tooltip>
                                        );
                                    })()}

                                    {/* Content */}
                                    <Box sx={{ flex: 1, minWidth: 0, ml: 1.75, pt: 0.5 }}>
                                        <Stack
                                            direction="row"
                                            alignItems="flex-start"
                                            spacing={1.5}
                                            role={isExpandable ? 'button' : undefined}
                                            tabIndex={isExpandable ? 0 : undefined}
                                            aria-expanded={isExpandable ? isExpanded : undefined}
                                            sx={{
                                                cursor: isExpandable ? 'pointer' : 'default',
                                                outline: 'none',
                                                '&:focus-visible': isExpandable ? {
                                                    boxShadow: '0 0 0 2px rgba(24,144,255,0.25)',
                                                    borderRadius: 1,
                                                } : {},
                                                '&:hover .timeline-title': isExpandable ? { color: 'primary.main' } : {},
                                            }}
                                            onClick={isExpandable ? () => setExpandedId(isExpanded ? null : rowKey) : undefined}
                                            onKeyDown={isExpandable ? (e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    setExpandedId(isExpanded ? null : rowKey);
                                                }
                                            } : undefined}
                                        >
                                            <Typography
                                                className="timeline-title"
                                                sx={{
                                                    flex: 1,
                                                    minWidth: 0,
                                                    fontSize: '0.8125rem',
                                                    fontWeight: 500,
                                                    color: 'text.primary',
                                                    lineHeight: 1.4,
                                                    transition: 'color 0.15s',
                                                    ...(isExpandable && !isExpanded && {
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                    }),
                                                    ...(isExpandable && isExpanded && {
                                                        whiteSpace: 'pre-wrap',
                                                        wordBreak: 'break-word',
                                                    }),
                                                }}
                                            >
                                                {title || '—'}
                                            </Typography>

                                            {status && (
                                                <Tooltip title={buildStatusTooltip(status)} placement="left" TransitionComponent={Zoom} TransitionProps={{ timeout: 200 }}>
                                                    <Box sx={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: 0.5,
                                                        px: 0.875,
                                                        py: 0.25,
                                                        borderRadius: 0.75,
                                                        bgcolor: `${status.color}14`,
                                                        flexShrink: 0,
                                                        mt: '1px',
                                                        cursor: 'default',
                                                    }}>
                                                        <Iconify icon={status.icon} sx={{ width: 13, height: 13, color: status.color }} />
                                                        <Typography sx={{
                                                            fontSize: '0.6875rem',
                                                            fontWeight: 600,
                                                            color: status.color,
                                                            lineHeight: 1,
                                                            whiteSpace: 'nowrap',
                                                            letterSpacing: 0.1,
                                                        }}>
                                                            {status.label}
                                                        </Typography>
                                                    </Box>
                                                </Tooltip>
                                            )}
                                        </Stack>

                                        {secondaryText && (
                                            <Typography sx={{ mt: 0.25, fontSize: '0.75rem', color: 'text.secondary', lineHeight: 1.4 }}>
                                                {secondaryText}
                                            </Typography>
                                        )}

                                        {act._transitionDetail && (() => {
                                            // M-3: omit "from {from}" when the source is null/empty so the
                                            // sub-line reads "Moved to X" rather than "Moved from  to X".
                                            const fromLabel = humanizeTreatmentState(act._transitionDetail.source);
                                            const toLabel = humanizeTreatmentState(act._transitionDetail.target);
                                            return (
                                                <Typography sx={{ mt: 0.375, fontSize: '0.75rem', color: 'text.secondary', lineHeight: 1.4 }}>
                                                    {fromLabel ? `Moved from ${fromLabel} to ${toLabel}` : `Moved to ${toLabel}`}
                                                </Typography>
                                            );
                                        })()}

                                        <Stack direction="row" alignItems="center" spacing={0.875} sx={{ mt: 0.625, flexWrap: 'wrap', rowGap: 0.5 }}>
                                            {actorName && (
                                                <Stack direction="row" alignItems="center" spacing={0.625}>
                                                    <CustomAvatar
                                                        src={act?.actor?.avatar || GetPracticeAvatar(practiceTreatment, practiceId)[0]?.logo}
                                                        name={act?.actor?.first_name}
                                                        sx={{ width: 18, height: 18, fontSize: '0.6rem' }}
                                                    />
                                                    <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary', fontWeight: 500 }}>
                                                        {actorName}
                                                    </Typography>
                                                </Stack>
                                            )}
                                            {actorName && dateStr && (
                                                <Box sx={{ width: 2.5, height: 2.5, bgcolor: 'text.disabled', borderRadius: '50%', flexShrink: 0 }} />
                                            )}
                                            {dateStr && (
                                                <Typography sx={{ fontSize: '0.6875rem', color: 'text.disabled', fontWeight: 500 }}>
                                                    {dateStr}
                                                </Typography>
                                            )}
                                            {callOutcome && (
                                                <>
                                                    <Box sx={{ width: 2.5, height: 2.5, bgcolor: 'text.disabled', borderRadius: '50%', flexShrink: 0 }} />
                                                    <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary', fontWeight: 500 }}>
                                                        {CALL_OUTCOME_LABEL[callOutcome] || callOutcome}
                                                        {callDurationText ? ` · ${callDurationText}` : ''}
                                                    </Typography>
                                                </>
                                            )}
                                            {additionalNotes && (
                                                <>
                                                    <Box sx={{ width: 2.5, height: 2.5, bgcolor: 'text.disabled', borderRadius: '50%', flexShrink: 0 }} />
                                                    <Tooltip
                                                        title={getNoteDisplayText(additionalNotes)}
                                                        placement="bottom"
                                                        TransitionComponent={Zoom}
                                                        TransitionProps={{ timeout: 200 }}
                                                    >
                                                        <Typography
                                                            tabIndex={0}
                                                            aria-label={`Note: ${getNoteDisplayText(additionalNotes)}`}
                                                            sx={{
                                                                fontSize: '0.6875rem',
                                                                color: 'text.secondary',
                                                                fontStyle: 'italic',
                                                                fontWeight: 500,
                                                                maxWidth: 200,
                                                                whiteSpace: 'nowrap',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                outline: 'none',
                                                                '&:focus-visible': {
                                                                    boxShadow: '0 0 0 2px rgba(24,144,255,0.25)',
                                                                    borderRadius: '4px',
                                                                },
                                                            }}
                                                        >
                                                            {parseAINote(additionalNotes) ? '🤖 AI Summary' : `“${capitalizeFirstLetter(additionalNotes).slice(0, 60)}${additionalNotes.length > 60 ? '…' : ''}”`}
                                                        </Typography>
                                                    </Tooltip>
                                                </>
                                            )}
                                        </Stack>
                                        {isTimelineCall(act) && <TimelineCallEntry act={act} practiceId={practiceId} />}
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                ))}

                {hasMore && (
                    <Box sx={{ textAlign: 'center', py: 1.5, mt: 1 }}>
                        <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled', fontWeight: 500 }}>
                            Showing {totalLoaded} of {totalAvailable} — scroll to load more
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default Activities;
